import { useState } from "react";
import type { Dispatch, SetStateAction } from "react";
import type { Task, List, Tag, TaskFilter, NewTaskFilter } from "../types";
import type { DialogContextValue } from "../contexts/DialogContext";
import type { DeleteListAction } from "../components/dialogs/DeleteConfirmDialog";
import { DEFAULT_LIST_EMOJI, DEFAULT_TAG_COLOR } from "../constants/defaults";

export interface EditingList extends List {
  newName?: string;
}

export interface EditingTag extends Tag {
  newName?: string;
}

export interface UseListTagManagementArgs {
  tasks: Task[];
  lists: List[];
  tags: Tag[];
  dialog: DialogContextValue;
  addListToDb: (data: { name: string; emoji: string }) => Promise<List | null>;
  updateListInDb: (id: string, data: { name?: string; emoji?: string }) => Promise<boolean>;
  deleteListFromDb: (id: string) => Promise<boolean>;
  moveListTasks: (fromListId: string, toListId: string) => Promise<boolean>;
  deleteAllTasksInList: (listId: string) => Promise<boolean>;
  addTagToDb: (data: { name: string; color: string }) => Promise<Tag | null>;
  updateTagInDb: (id: string, data: { name?: string; color?: string }) => Promise<boolean>;
  deleteTagFromDb: (id: string) => Promise<boolean>;
  addFilterToDb: (data: NewTaskFilter) => Promise<TaskFilter | null>;
  updateFilterInDb: (id: string, data: Partial<NewTaskFilter>) => Promise<boolean>;
}

export interface UseListTagManagementResult {
  editingList: EditingList | null;
  setEditingList: (list: EditingList | null) => void;
  editingTag: EditingTag | null;
  setEditingTag: (tag: EditingTag | null) => void;
  editingFilter: TaskFilter | null;
  setEditingFilter: Dispatch<SetStateAction<TaskFilter | null>>;
  newListName: string;
  setNewListName: (name: string) => void;
  newListEmoji: string;
  setNewListEmoji: (emoji: string) => void;
  newTagName: string;
  setNewTagName: (name: string) => void;
  newTagColor: string;
  setNewTagColor: (color: string) => void;
  addList: () => Promise<void>;
  updateList: (oldName: string, newName: string, newEmoji: string) => Promise<void>;
  buildListBreakdown: (listName: string) => { open: number; completed: number; archived: number };
  deleteList: (listName: string, action?: DeleteListAction) => Promise<void>;
  addTag: () => Promise<void>;
  updateTag: (id: string, newName: string, newColor: string) => Promise<void>;
  deleteTag: (tagId: string) => Promise<void>;
  handleSaveFilter: (filterData: NewTaskFilter) => Promise<void>;
}

export function useListTagManagement({
  tasks,
  lists,
  tags,
  dialog,
  addListToDb,
  updateListInDb,
  deleteListFromDb,
  moveListTasks,
  deleteAllTasksInList,
  addTagToDb,
  updateTagInDb,
  deleteTagFromDb,
  addFilterToDb,
  updateFilterInDb,
}: UseListTagManagementArgs): UseListTagManagementResult {
  const [editingList, setEditingList] = useState<EditingList | null>(null);
  const [editingTag, setEditingTag] = useState<EditingTag | null>(null);
  const [editingFilter, setEditingFilter] = useState<TaskFilter | null>(null);
  const [newListName, setNewListName] = useState("");
  const [newListEmoji, setNewListEmoji] = useState(DEFAULT_LIST_EMOJI);
  const [newTagName, setNewTagName] = useState("");
  const [newTagColor, setNewTagColor] = useState(DEFAULT_TAG_COLOR);

  const addList = async () => {
    if (!newListName.trim()) return;
    if (lists.some((l) => l.name === newListName)) return;

    await addListToDb({ name: newListName, emoji: newListEmoji });
    setNewListName("");
    setNewListEmoji(DEFAULT_LIST_EMOJI);
    dialog.close();
  };

  const updateList = async (
    oldName: string,
    newName: string,
    newEmoji: string,
  ) => {
    if (!newName.trim()) return;

    const list = lists.find((l) => l.name === oldName);
    if (list) {
      await updateListInDb(list.id, { name: newName, emoji: newEmoji });
    }
    setEditingList(null);
  };

  // Build the open/completed/archived breakdown for a list so the delete
  // dialog can warn about tasks the default view normally hides.
  const buildListBreakdown = (listName: string) => {
    const inList = tasks.filter((t) => t.list === listName);
    let open = 0;
    let completed = 0;
    let archived = 0;
    for (const t of inList) {
      if (t.archived) archived++;
      else if (t.completed) completed++;
      else open++;
    }
    return { open, completed, archived };
  };

  const deleteList = async (
    listName: string,
    action: DeleteListAction = { type: "remove-from-list" },
  ) => {
    const list = lists.find((l) => l.name === listName);
    if (list) {
      if (action.type === "move-tasks") {
        await moveListTasks(list.id, action.targetListId);
      } else if (action.type === "delete-all") {
        await deleteAllTasksInList(list.id);
      }
      await deleteListFromDb(list.id);
    }
    dialog.close();
  };

  const addTag = async () => {
    if (!newTagName.trim()) return;
    if (tags.some((tag) => tag.name === newTagName)) return;

    await addTagToDb({ name: newTagName, color: newTagColor });
    setNewTagName("");
    setNewTagColor(DEFAULT_TAG_COLOR);
    dialog.close();
  };

  const updateTag = async (id: string, newName: string, newColor: string) => {
    if (!newName.trim()) return;

    await updateTagInDb(id, { name: newName, color: newColor });
    setEditingTag(null);
  };

  const deleteTag = async (tagId: string) => {
    await deleteTagFromDb(tagId);
    dialog.close();
  };

  const handleSaveFilter = async (filterData: NewTaskFilter) => {
    if (editingFilter) {
      await updateFilterInDb(editingFilter.id, filterData);
    } else {
      await addFilterToDb(filterData);
    }
    dialog.close();
    setEditingFilter(null);
  };

  return {
    editingList,
    setEditingList,
    editingTag,
    setEditingTag,
    editingFilter,
    setEditingFilter,
    newListName,
    setNewListName,
    newListEmoji,
    setNewListEmoji,
    newTagName,
    setNewTagName,
    newTagColor,
    setNewTagColor,
    addList,
    updateList,
    buildListBreakdown,
    deleteList,
    addTag,
    updateTag,
    deleteTag,
    handleSaveFilter,
  };
}
